import { Stack } from "@mui/system";
import { Autocomplete, Button, Grid, TextField } from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTextDomainContext, useUser } from "@/app/providers";
import {
  TENANTS_LIST,
  useAddTenant,
  useAddUserToTenant,
  useCountries,
  useGetPartnerByKey,
  useTenantList,
  useUpdateTenant,
  useUpdateUser,
} from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { isEqual } from "lodash";
import { func } from "prop-types";
import { getPartnerDetails } from "@/utils/partners/getPartnerDetails";
import { useLazyQuery } from "@apollo/client/react";

export const CountryTimezoneForm = (props: any) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { countries, loading: countriesLoading } = useCountries();
  const { user, setUser } = useUser();
  const partnerDetails = getPartnerDetails();
  const { tenantsList, loading: loadingTenantsList } = useTenantList({
    email: user?.email,
    ignoreRole: true,
  });

  const { tenantsList: allTenants } = useTenantList({});

  const { partner, loading: loadingPartner } = useGetPartnerByKey(
    partnerDetails.partnerKey,
  );

  const [getLatestTenants] = useLazyQuery(TENANTS_LIST, {});

  const { addUserToTenant } = useAddUserToTenant();

  const currentDate = useMemo(() => {
    return new Date();
  }, []);

  const { addTenant, loading: updatingAdd } = useAddTenant(
    () => {},
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateTenant, loading: updatingEdit } = useUpdateTenant(
    () => {},
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const initialValue = useMemo(() => {
    const defaultCountry =
      countries.find((c: any) => c.code === user?.countryCode) ||
      countries[0] ||
      null;

    return {
      fullName: user?.name || "",
      businessName: "",
      inputCountry: defaultCountry,
      subscriptionExpireAt: new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        currentDate.getDate(),
      ),
    };
  }, [countries, currentDate, user]);

  const hasTenant =
    Boolean(tenantsList?.length) ||
    Boolean(user?.currentTenantId && user.currentTenantId !== "none");

  const [fullName, setFullName] = useState(initialValue.fullName);
  const [inputCountry, setInputCountry] = useState(initialValue.inputCountry);
  const [businessName, setBusinessName] = useState(initialValue.businessName);

  useEffect(() => {
    if (user?.name && !fullName) {
      setFullName(user.name);
    }
  }, [user?.name, fullName]);

  useEffect(() => {
    if (!inputCountry?.code && countries.length) {
      const defaultCountry =
        countries.find((c: any) => c.code === user?.countryCode) ||
        countries[0];
      if (!isEqual(inputCountry, defaultCountry)) {
        setTimeout(() => {
          setInputCountry(defaultCountry);
        }, 0);
      }
    }
  }, [countries, inputCountry, user?.countryCode]);

  const handleClose = () => {
    props.handleClose();
  };

  const { updateUser, loading: updating } = useUpdateUser(
    () => {
      handleClose();
      enqueueSnackbar(gettext("The user has been updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        if (import.meta.env.VITE_NODE_ENV !== "production") {
          enqueueSnackbar(`Backend Error: ${error}`, {
            variant: "error",
          });
        }
      }
    },
  );

  const submitHandler = (e: React.FormEvent) => {
    e.preventDefault();

    const name = fullName.trim() || user?.name || "";
    const countryCode = user?.countryCode || inputCountry?.code;
    const timezone =
      user?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      "Europe/Copenhagen";

    const variables: any = {
      userId: user.id,
      name,
      countryCode,
      timezone,
    };

    const updatedUser = {
      ...user,
      ...variables,
    };

    updateUser({
      variables,
      refetchQueries: [
        "AllUsers",
        "SystemUsers",
        "PartnerUsers",
        "CompanyUsers",
        "useMe",
      ],
    }).then(() => {
      setUser(updatedUser);
    });

    if (!hasTenant) {
      const groupName = businessName.trim();
      const tenantVariables = {
        name: groupName,
        description: "",
        subscriptionPlan: "trial",
        dataLimit: "",
        subscriptionExpireAt: initialValue.subscriptionExpireAt,
        partner: partner?.id,
        status: "Active",
      };

      const tenant = allTenants?.find(
        (tenant: any) => tenant.name === groupName,
      );

      if (tenant?.Id && !tenant?.users?.length) {
        (variables as any).tenantId = tenant.Id;
        updateTenant({
          variables: variables,
          refetchQueries: ["TenantsList"],
        }).then(() => {
          userToTenant(groupName);
        });
      } else {
        addTenant({
          variables: tenantVariables,
        }).then(() => {
          userToTenant(groupName);
        });
      }
    }
  };

  const userToTenant = (nameToFind: string) => {
    getLatestTenants().then((latestTenants: any) => {
      const tenant = latestTenants?.data?.tenantsList?.find(
        (tenant: any) => tenant.name === nameToFind,
      );
      const variables = {
        tenantId: tenant?.id,
        partnerId: partner?.id,
        email: user?.email,
        roleKey: "company-owner",
        languageCode: user?.lang,
        partnerKey: partnerDetails.partnerKey,
        notifyUser: false,
      };
      addUserToTenant({
        variables,
      }).then();
    });
  };

  const handleBusinessNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setBusinessName(event.target.value);
  };

  return (
    <form onSubmit={submitHandler}>
      <Stack
        spacing={4}
        sx={{
          height: "100%",
        }}
      >
        {!user?.name?.trim() && (
          <Grid size={{ xs: 12 }}>
            <TextField
              type="text"
              label={gettext("Full Name")}
              placeholder={gettext("Enter your full name")}
              size="small"
              required
              fullWidth
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </Grid>
        )}

        {!user?.countryCode && (
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              disablePortal
              options={countries as any}
              getOptionLabel={(option: any) =>
                option?.name || String(option) || ""
              }
              getOptionKey={(option: any) =>
                option?.code || String(option) || ""
              }
              value={inputCountry || ""}
              onChange={(e, newValue) => {
                setInputCountry(newValue as any);
              }}
              size="small"
              fullWidth
              isOptionEqualToValue={(option: any, value: any) =>
                String((option?.value ?? option) as any).toLowerCase() ===
                String((value?.value ?? value) as any).toLowerCase()
              }
              renderInput={(params) => (
                <TextField {...params} required label={gettext("Country")} />
              )}
            />
          </Grid>
        )}

        {!hasTenant && (
          <Grid size={{ xs: 12 }}>
            <TextField
              type="text"
              label={gettext("Corporate Group")}
              placeholder={gettext("Corporate Group")}
              size="small"
              required
              fullWidth
              value={businessName || ""}
              onChange={handleBusinessNameChange}
            />
          </Grid>
        )}
        <Grid size={{ xs: 12 }} sx={{ textAlign: "center" }}>
          <Button
            variant="contained"
            loading={
              updating ||
              countriesLoading ||
              updatingAdd ||
              loadingPartner ||
              updatingEdit ||
              loadingTenantsList
            }
            fullWidth
            type="submit"
          >
            {gettext("Save")}
          </Button>
        </Grid>
      </Stack>
    </form>
  );
};

CountryTimezoneForm.propTypes = {
  handleClose: func.isRequired,
};

export const AccountSetupForm = CountryTimezoneForm;
